// Conversations: WhatsApp threads by phone number and Instagram DM threads by @username, side by side,
// as in the reference product. Voice call bookings live on Call Records, not here.
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FaInstagram, FaWhatsapp } from 'react-icons/fa';
import api, { formatPhone } from '../services/api';
import ChatModal from '../components/ChatModal';
import {
  ChevronIcon,
  ChipRow,
  INSTAGRAM_COLOR,
  InterestTag,
  InterventionBadge,
  LeadFlag,
  NO_SHOW_STAGE,
  NoShowTag,
  STAGES,
  UpcomingTag,
  Loader,
  Pagination,
  RefreshIcon,
  SearchBox,
  ToggleChip,
  ViewButton,
  WelcomeBar,
  instagramHandle,
  usePagination,
} from '../components/ui';

const POLL_MS = 10000;
const FLAG_CHIPS = [{ key: '', label: 'All statuses' }, ...[...STAGES, NO_SHOW_STAGE].map((s) => ({ key: s.key, label: s.label }))];

const CHANNELS = {
  whatsapp: {
    icon: <FaWhatsapp size={18} color="#25D366" />,
    title: 'Open WhatsApp conversation threads by phone number',
    search: 'Search name, phone num...',
    who: 'Phone number',
  },
  instagram: {
    icon: <FaInstagram size={18} color={INSTAGRAM_COLOR} />,
    title: 'Open Instagram DM threads by username',
    search: 'Search username, name…',
    who: 'Instagram username',
  },
};

function Accordion({ icon, title, open, onToggle, children }) {
  return (
    <div className="nye-accordion">
      <div className="nye-accordion-head" onClick={onToggle} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && onToggle()}>
        <span className="nye-row" style={{ gap: 12 }}>
          {icon}
          {title}
        </span>
        <ChevronIcon up={open} />
      </div>
      {open ? <div className="nye-accordion-body">{children}</div> : null}
    </div>
  );
}

function ThreadList({ channel, threads, onOpen, onRefresh }) {
  const cfg = CHANNELS[channel];
  const [open, setOpen] = useState(true);
  const [search, setSearch] = useState('');
  const [flag, setFlag] = useState('');
  const [humanOnly, setHumanOnly] = useState(false);
  const [botOnly, setBotOnly] = useState(false);
  const [bookedOnly, setBookedOnly] = useState(false);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase().replace(/^[@+]/, '');
    const rows = Array.isArray(threads) ? threads : [];
    return rows.filter((t) => {
      if (flag && t.flag !== flag) return false;
      if (humanOnly && !t.handoff_open) return false;
      if (botOnly && !t.bot_detected) return false;
      if (bookedOnly && !(t.upcoming || []).length) return false;
      if (!q) return true;
      return [t.name, t.instagram_username, t.phone].some((v) => String(v || '').toLowerCase().includes(q));
    });
  }, [threads, search, flag, humanOnly, botOnly, bookedOnly]);
  const page = usePagination(filtered, 25);
  const isInstagram = channel === 'instagram';

  return (
    <Accordion icon={cfg.icon} title={cfg.title} open={open} onToggle={() => setOpen((v) => !v)}>
      <div className="nye-row" style={{ gap: 10, marginBottom: 14 }}>
        <SearchBox value={search} onChange={setSearch} placeholder={cfg.search} style={{ maxWidth: 'none' }} />
        <button type="button" className="nye-btn nye-btn--outline nye-btn--sm" onClick={onRefresh}>
          <RefreshIcon /> Refresh
        </button>
      </div>
      <ChipRow chips={FLAG_CHIPS} value={flag} onChange={setFlag} />
      <div className="nye-row nye-row--wrap" style={{ marginBottom: 14 }}>
        <ToggleChip active={humanOnly} onClick={() => setHumanOnly((v) => !v)}>
          🆘 Human intervention needed
        </ToggleChip>
        <ToggleChip active={botOnly} onClick={() => setBotOnly((v) => !v)}>
          🤖 Bot detected
        </ToggleChip>
        <ToggleChip active={bookedOnly} onClick={() => setBookedOnly((v) => !v)}>
          📅 Has upcoming booking
        </ToggleChip>
      </div>
      <div className="nye-count-line">
        {filtered.length} conversation{filtered.length !== 1 ? 's' : ''}
      </div>

      <div className="nye-list-head nye-conv-head">
        <div className="nye-conv-name">{isInstagram ? 'Instagram username' : 'Name'}</div>
        <div className="nye-conv-who">{isInstagram ? 'Mobile' : cfg.who}</div>
        <div className="nye-conv-status">Status</div>
        <div className="nye-conv-view">Chat view</div>
      </div>
      <div style={{ maxHeight: 420, overflowY: 'auto' }}>
        {page.paginated.length === 0 ? (
          <div className="nye-list-row" style={{ justifyContent: 'center', color: 'var(--nye-soft)', flexDirection: 'column', gap: 4 }}>
            <span>No conversations found</span>
            {isInstagram && threads.length === 0 ? (
              <span className="nye-small">Instagram DMs appear here once the Instagram webhook is connected.</span>
            ) : null}
          </div>
        ) : (
          page.paginated.map((t) => {
            const handle = instagramHandle(t.instagram_username);
            const title = isInstagram ? handle || t.name || 'Username not available' : t.name || '—';
            return (
              <div key={`${channel}-${t.customer_id}`} className="nye-list-row nye-conv-row">
                <div className="nye-conv-name">
                  <div className="nye-strong nye-ellipsis">{title}</div>
                  {isInstagram && t.name && handle && t.name !== t.instagram_username ? (
                    <div className="nye-small nye-muted nye-ellipsis">{t.name}</div>
                  ) : null}
                  <div className="nye-small nye-muted nye-ellipsis" title={t.last_message}>
                    {t.last_direction === 'out' ? 'Nyafaz: ' : ''}
                    {t.last_message}
                  </div>
                </div>
                <div className={`nye-conv-who${t.phone ? '' : ' nye-muted'}`}>
                  {t.phone ? formatPhone(t.phone) : isInstagram ? 'Not shared yet' : '—'}
                </div>
                <div className="nye-conv-status">
                  <LeadFlag status={t.flag} reason={t.flag_reason} />
                  {t.flag !== 'booked' ? <InterestTag service={t.interest_service} /> : null}
                  <UpcomingTag bookings={t.upcoming} />
                  <NoShowTag count={t.no_shows} />
                  {t.handoff_open ? <InterventionBadge compact /> : null}
                  {t.bot_detected ? (
                    <span className="nye-pill nye-pill--danger nye-pill--sm" title={t.bot_reason}>🤖 Bot detected</span>
                  ) : t.agent_paused ? (
                    <span className="nye-pill nye-pill--warn nye-pill--sm">AI paused</span>
                  ) : null}
                </div>
                <div className="nye-conv-view">
                  <ViewButton onClick={() => onOpen(t.customer_id, channel)} title="Open chat" />
                </div>
              </div>
            );
          })
        )}
      </div>
      {filtered.length > 25 ? <Pagination {...page} /> : null}
    </Accordion>
  );
}

export default function Conversations() {
  const [searchParams, setSearchParams] = useSearchParams();
  const openCustomer = Number(searchParams.get('customer')) || null;
  const openChannel = searchParams.get('channel') === 'instagram' ? 'instagram' : 'whatsapp';

  const [threads, setThreads] = useState({ whatsapp: [], instagram: [] });
  const [loading, setLoading] = useState(true);

  const load = (silent) => {
    if (!silent) setLoading(true);
    return Promise.all([api.listConversations('whatsapp'), api.listConversations('instagram')])
      .then(([whatsapp, instagram]) =>
        setThreads({
          whatsapp: Array.isArray(whatsapp) ? whatsapp : [],
          instagram: Array.isArray(instagram) ? instagram : [],
        }),
      )
      .catch((e) => !silent && toast.error(e.message || 'Failed to load conversations'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(false);
    const t = setInterval(() => load(true), POLL_MS);
    return () => clearInterval(t);
  }, []);

  const openChat = (customerId, channel) => setSearchParams({ customer: String(customerId), channel });
  const closeChat = () => setSearchParams({});
  const channelThreads = Array.isArray(threads[openChannel]) ? threads[openChannel] : [];
  const activeThread = channelThreads.find((t) => t.customer_id === openCustomer);

  return (
    <div className="nye-fade">
      <WelcomeBar title="Conversations" sub="WhatsApp and Instagram chats answered by the Nyafaz AI agent." />

      {loading ? (
        <Loader />
      ) : (
        <div className="nye-g2 nye-g2--auto nye-conv-grid" style={{ gap: 24 }}>
          <ThreadList channel="whatsapp" threads={threads.whatsapp} onOpen={openChat} onRefresh={() => load(false)} />
          <ThreadList channel="instagram" threads={threads.instagram} onOpen={openChat} onRefresh={() => load(false)} />
        </div>
      )}

      {openCustomer ? (
        <ChatModal customerId={openCustomer} channel={openChannel} flag={activeThread?.flag} onClose={closeChat} onChanged={() => load(true)} />
      ) : null}
    </div>
  );
}
